(() => {
  'use strict';

  const motionEnabled = () => document.body.classList.contains('animations-enabled') && !matchMedia('(prefers-reduced-motion: reduce)').matches;

  class ArticleToc extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const body = document.getElementById(this.dataset.target);
      const list = this.querySelector('ol');
      this.headings = [...(body?.querySelectorAll('h2') || [])].filter(heading => !heading.closest('[data-article-editorial]'));
      if (!list || !this.headings.length) { this.hidden = true; return; }
      this.hidden = false;
      const counts = new Map();
      document.querySelectorAll('[id]').forEach(node => counts.set(node.id, (counts.get(node.id) || 0) + 1));
      const assigned = new Set();
      this.links = this.headings.map((heading, index) => {
        let id = heading.id;
        if (!id || /\s/.test(id) || counts.get(id) !== 1 || assigned.has(id)) {
          id = `${this.dataset.target}-part-${index + 1}`;
          let suffix = 1;
          while (document.getElementById(id) || assigned.has(id)) id = `${this.dataset.target}-part-${index + 1}-${++suffix}`;
          heading.id = id;
        }
        assigned.add(id);
        const item = document.createElement('li');
        const link = document.createElement('a');
        const number = document.createElement('span');
        const label = document.createElement('span');
        link.href = `#${encodeURIComponent(id)}`;
        link.dataset.articleTocLink = String(index);
        number.className = 'olea-article__toc-index';
        number.setAttribute('aria-hidden', 'true');
        number.textContent = String(index + 1).padStart(2, '0');
        label.className = 'olea-article__toc-text';
        label.textContent = heading.textContent;
        link.append(number, label);
        item.append(link);
        return { item, link };
      });
      list.replaceChildren(...this.links.map(value => value.item));
      const count = this.querySelector('[data-article-toc-count]');
      if (count) count.textContent = ` · ${this.headings.length} ${this.headings.length === 1 ? this.dataset.countSingular || '' : this.dataset.countPlural || ''}`.trimEnd();
      this.details = this.querySelector('details');
      this.breakpoint = matchMedia('(min-width: 750px)');
      const resizeDetails = () => { if (this.details) this.details.open = this.breakpoint.matches; };
      resizeDetails();
      this.breakpoint.addEventListener?.('change', resizeDetails, { signal });
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-article-toc-link]');
        if (!link || !this.contains(link)) return;
        const index = Number(link.dataset.articleTocLink);
        const heading = this.headings[index];
        if (!heading) return;
        event.preventDefault();
        this.activate(index);
        heading.scrollIntoView({ block: 'start', behavior: motionEnabled() ? 'smooth' : 'instant' });
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        const url = new URL(location.href);
        url.hash = heading.id;
        history.replaceState({}, '', url.pathname + url.search + url.hash);
      }, { signal });
      this.addEventListener('keydown', event => {
        const link = event.target.closest('[data-article-toc-link]');
        if (!link || !['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const index = Number(link.dataset.articleTocLink);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? this.links.length - 1 : Math.max(0, Math.min(this.links.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1)));
        this.links[next]?.link.focus();
      }, { signal });
      const schedule = () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => { this.scrollFrame = null; this.updateActive(); });
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      this.updateActive();
    }

    updateActive() {
      const header = document.querySelector('.site-header');
      const offset = Math.max(0, header?.getBoundingClientRect().height || 0) + 48;
      let index = 0;
      this.headings.forEach((heading, current) => { if (heading.getBoundingClientRect().top <= offset) index = current; });
      this.activate(index);
    }

    activate(index) {
      this.links?.forEach(({ link }, current) => {
        link.classList.toggle('is-active', current === index);
        if (current === index) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
    }
  }

  class OleaArticle extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.positionEditorial();
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-article-copy]');
        if (!button || !this.contains(button)) return;
        event.preventDefault();
        this.copyLink(button);
      }, { signal: this.controller.signal });
    }

    positionEditorial() {
      const body = this.querySelector('[data-article-body]') || this.querySelector('.olea-article__body .rte');
      if (!body) return;
      const headings = [...body.querySelectorAll('h2')].filter(heading => !heading.closest('[data-article-editorial]'));
      this.querySelectorAll('[data-article-editorial]').forEach(module => {
        const index = Number(module.dataset.articleAfterHeading);
        const next = Number.isInteger(index) && index > 0 ? headings[index] : null;
        if (next) next.before(module);
        else body.append(module);
        module.hidden = false;
      });
    }

    shareUrl() {
      const source = this.dataset.articleUrl || document.querySelector('link[rel="canonical"]')?.href || location.href;
      const url = new URL(source, location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) throw new Error('Invalid article URL');
      url.searchParams.delete('preview_theme_id');
      url.searchParams.delete('pb');
      url.hash = '';
      return url.href;
    }

    async copyLink(button) {
      if (button.disabled) return;
      const status = this.querySelector('[data-article-share-status]');
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (status) status.textContent = '';
      try {
        const url = this.shareUrl();
        if (window.Olea?.copyText) await window.Olea.copyText(url);
        else if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(url);
        else throw new Error('Clipboard unavailable');
        if (this.isConnected && status) status.textContent = button.dataset.copySuccess || '';
      } catch {
        if (this.isConnected) {
          if (status) status.textContent = button.dataset.copyError || '';
          await this.manualCopy(button);
        }
      } finally {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }

    async manualCopy(button) {
      let url;
      try { url = this.shareUrl(); } catch { return; }
      if (this.shareDialog) await (window.OleaOverlayMotion?.hide(this.shareDialog) ?? this.shareDialog.close());
      if (!this.isConnected) return;
      const dialog = document.createElement('dialog');
      const title = document.createElement('h2');
      const input = document.createElement('input');
      const close = document.createElement('button');
      dialog.className = 'olea-article__share-dialog';
      title.textContent = button.dataset.manualTitle || button.getAttribute('aria-label') || '';
      title.id = `ArticleShare-${this.id || 'link'}`;
      dialog.setAttribute('aria-labelledby', title.id);
      input.type = 'url';
      input.readOnly = true;
      input.value = url;
      input.setAttribute('aria-label', button.dataset.urlLabel || title.textContent);
      close.type = 'button';
      close.className = 'button button--outline';
      close.textContent = button.dataset.closeLabel || 'OK';
      close.addEventListener('click', () => { window.OleaOverlayMotion?.hide(dialog) ?? dialog.close(); });
      dialog.addEventListener('click', event => { if (event.target === dialog) window.OleaOverlayMotion?.hide(dialog) ?? dialog.close(); });
      dialog.addEventListener('close', () => { dialog.remove(); if (button.isConnected) button.focus({ preventScroll: true }); if (this.shareDialog === dialog) this.shareDialog = null; }, { once: true });
      dialog.append(title, input, close);
      this.append(dialog);
      this.shareDialog = dialog;
      window.OleaOverlayMotion?.show(dialog) ?? dialog.showModal();
      input.focus();
      input.select();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      if (this.shareDialog) window.OleaOverlayMotion?.hide(this.shareDialog, { immediate: true }) ?? this.shareDialog.close();
    }
  }

  if (!customElements.get('article-toc')) customElements.define('article-toc', ArticleToc);
  if (!customElements.get('olea-article')) customElements.define('olea-article', OleaArticle);
})();
